/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { formatMoney } from "../../../../utils/format-utils";
import { ReviewModalVariant } from "./ReviewModal";

export const campaignFieldConfig = (variant: ReviewModalVariant) => ({
  objective: { label: "Campaign Objective" },
  special_ad_categories: {
    label: "Special Ad Categories",
    condition: (value: any): boolean => Array.isArray(value) && value.length > 0,
  },
  special_ad_category_country: {
    label: "Special Ad Categories Country",
    condition: (value: any): boolean => value !== null,
  },
  bid_strategy: {
    label: "Bid Strategy",
    condition: (value: any): boolean => Boolean(value),
  },
  lifetime_budget: {
    label: "Lifetime Budget",
    formatter: (value: any): string => formatMoney((value || 0) / 100),
  },
  daily_budget: {
    label: "Daily Budget",
    formatter: (value: any): string => formatMoney((value || 0) / 100),
  },
  spend_cap: {
    label: "Spend Cap",
    formatter: (value: any): string => formatMoney((value || 0) / 100),
  },
  lastuser: {
    label: "Last Modified By",
    condition: (value: any): boolean => variant !== "editing" && Boolean(value),
  },
  created: {
    label: "Created On",
    condition: (value: any): boolean => variant !== "editing" && Boolean(value),
    formatter: (value: any): string => new Date(value).toLocaleString(),
  },
});

export const adSetFieldConfig = (variant: ReviewModalVariant) => ({
  campaign_name: { label: "Campaign" },
  pixel_name: {
    label: "Pixel",
    formatter: (value: any, data: any): string => value || data.pixel_id,
  },
  custom_conversion_name: {
    label: "Conversion Event",
    formatter: (value: any, data: any): string =>
      value || data.custom_conversion_id || data.custom_event_type,
  },
  optimization_goal: { label: "Optimization Goal" },
  bid_strategy: {
    label: "Bid Strategy",
    condition: (value: any): boolean => Boolean(value),
  },
  bid_amount: {
    label: "Bid Amount",
    formatter: (value: any): string => formatMoney((value || 0) / 100),
  },
  roas_average_floor: {
    label: "Min ROAS",
    formatter: (value: any): string => ((value || 0) / 10000).toFixed(3),
  },
  daily_budget: {
    label: "Daily Budget",
    formatter: (value: any): string => formatMoney((value || 0) / 100),
  },
  lifetime_budget: {
    label: "Lifetime Budget",
    formatter: (value: any): string => formatMoney((value || 0) / 100),
  },
  daily_spend_cap: {
    label: "Daily Spend Cap",
    formatter: (value: any): string => formatMoney((value || 0) / 100),
  },
  lifetime_spend_cap: {
    label: "Lifetime Spend Cap",
    formatter: (value: any): string => formatMoney((value || 0) / 100),
  },
  age_min: {
    label: "Age Min",
    condition: (value: any): boolean => Boolean(value),
  },
  age_max: {
    label: "Age Max",
    condition: (value: any): boolean => Boolean(value),
  },
  genders: {
    label: "Genders",
    condition: (value: any): boolean => Boolean(value),
    formatter: (value: number[]): string => {
      if (value[0] === 1) {
        return "Men";
      } else if (value[0] === 2) {
        return "Women";
      } else {
        return "All";
      }
    },
  },
  click_attribution_window: {
    label: "Click Attribution Window",
    condition: (value: any): boolean => Boolean(value),
  },
  view_attribution_window: {
    label: "View Attribution Window",
    condition: (value: any): boolean => Boolean(value),
  },
  publisher_platforms: {
    label: "Publisher Platforms",
    condition: (value: any): boolean => Array.isArray(value) && value.length > 0,
  },
  facebook_positions: {
    label: "Facebook Positions",
    condition: (value: any): boolean => Array.isArray(value) && value.length > 0,
  },
  instagram_positions: {
    label: "Instagram Positions",
    condition: (value: any): boolean => Array.isArray(value) && value.length > 0,
  },
  messenger_positions: {
    label: "Messenger Positions",
    condition: (value: any): boolean => Array.isArray(value) && value.length > 0,
  },
  audience_network_positions: {
    label: "Audience Network Positions",
    condition: (value: any): boolean => Array.isArray(value) && value.length > 0,
  },
  custom_audiences: {
    label: "Custom Audiences",
    formatter: (value: any, data: any): string => data.custom_audience_names || value,
  },
  excluded_custom_audiences: {
    label: "Excluded Custom Audiences",
    formatter: (value: any, data: any): string => data.excluded_custom_audience_names || value,
  },
  start_time: {
    label: "Start Time",
    condition: (value: any): boolean => Boolean(value),
    formatter: (value: any): string => new Date(value).toLocaleString(),
  },
  end_time: {
    label: "End Time",
    condition: (value: any): boolean => Boolean(value),
    formatter: (value: any): string => new Date(value).toLocaleString(),
  },
  lastuser: {
    label: "Last Modified By",
    condition: (value: any): boolean => variant !== "editing" && Boolean(value),
  },
  created: {
    label: "Created On",
    condition: (value: any): boolean => variant !== "editing" && Boolean(value),
    formatter: (value: any): string => new Date(value).toLocaleString(),
  },
});

export const adFieldConfig = (variant: ReviewModalVariant) => ({
  adset_name: { label: "Ad Set" },
  campaign_name: { label: "Campaign" },
  page_name: { label: "Facebook Page" },
  format: { label: "Creative Format" },
  asset_name: {
    label: "Asset",
    condition: (value: any): boolean => Boolean(value),
  },
  asset_name_square: {
    label: "Feed Asset",
    condition: (value: any): boolean => Boolean(value),
  },
  asset_name_horizontal: {
    label: "Horizontal Asset",
    condition: (value: any): boolean => Boolean(value),
  },
  asset_name_vertical: {
    label: "Vertical Asset",
    condition: (value: any): boolean => Boolean(value),
  },
  link: { label: "Link" },
  message: { label: "Primary Text" },
  headline: {
    label: (data: any): string => (data.format === "IMAGE" ? "Name" : "Title"),
  },
  description: { label: "Description" },
  call_to_action: { label: "CTA" },
  lastuser: {
    label: "Last Modified By",
    condition: (value: any): boolean => variant !== "editing" && Boolean(value),
  },
  created: {
    label: "Created On",
    condition: (value: any): boolean => variant !== "editing" && Boolean(value),
    formatter: (value: any): string => new Date(value).toLocaleString(),
  },
});
